import {
  GameComponentForGame,
  GameMoveData,
  GameMoveForEval,
  GeneralComponentForGame,
  PossiblyUnknownGameComponent,
  SystemLogMessage,
} from "../implementationTypes";
import {
  ComponentData,
  ComponentDataSource,
  ComponentPool,
  DeckInfo,
  GameComponent,
  GameSituationBackend,
  LogMessageData,
} from "../types";

let UA = navigator.userAgent;
export function isWebkit() {
  return /\b(iPad|iPhone|iPod)\b/.test(UA) && /WebKit/.test(UA) && !/Edge/.test(UA) && !window.MSStream;
}

export function isSafari() {
  return !!(
    navigator.vendor &&
    navigator.vendor.indexOf("Apple") > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf("CriOS") == -1 &&
    navigator.userAgent.indexOf("FxiOS") == -1
  );
}

export function mergeDeep(target: { [key: string]: any }, source: { [key: string]: any }) {
  for (let key of Object.keys(source)) {
    if (Array.isArray(source[key])) {
      if (!target[key]) {
        target[key] = [];
      }

      for (let i = 0; i < source[key].length; i++) {
        let value = source[key][i];
        if (value) {
          if (typeof value === "object") {
            if (!target[key][i]) {
              target[key][i] = {};
            }
            mergeDeep(target[key][i], value);
          } else {
            target[key][i] = value;
          }
        }
      }
    } else if (typeof source[key] === "object" && source[key] !== null) {
      if (!target[key]) {
        target[key] = {};
      }
      mergeDeep(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
}

// eslint-disable-next-line no-undef
export async function importAll(projectSlug: string) {
  // Glob all possible assets across projects
  const allModules = import.meta.glob("../../**/assets/**/*.*");
  let result = {} as { [key: string]: any };
  let allPromises = [];
  for (const path in allModules) {
    // Check if the path matches the desired projectSlug
    if (
      path.includes(`/../projects/${projectSlug}/assets/`) &&
      (path.endsWith(".png") ||
        path.endsWith(".jpg") ||
        path.endsWith(".jpeg") ||
        path.endsWith(".wav") ||
        path.endsWith(".webp"))
    ) {
      const localName = path.substring(`../../projects/${projectSlug}/assets/`.length);
      allPromises.push(
        allModules[path]().then((data: unknown) => {
          result[localName] = data;
        }),
      );
    }
  }
  await Promise.all(allPromises);
  return result;
}

export function getAllComponentDirectChildren(component: GameComponentForGame) {
  let result: GeneralComponentForGame[] = [];
  if (!component.children) {
    return result;
  }
  for (let positionName of Object.keys(component.children)) {
    for (let positionIndex of Object.keys(component.children[positionName])) {
      let position = component.children[positionName][positionIndex];
      if (Array.isArray(position)) {
        result.push(...position);
      } else {
        result.push(position);
      }
    }
  }
  return result;
}

export function getAllComponentChildren(component?: PossiblyUnknownGameComponent) {
  if (!component) {
    return [];
  }
  let result = [component];
  if (!component.children) {
    return result;
  }
  for (let c of getAllComponentDirectChildren(component)) {
    if (component.children) {
      result.push(...getAllComponentChildren(c as PossiblyUnknownGameComponent));
    }
  }
  return result;
}

export function logMessage(event: GameMoveData | SystemLogMessage, depth?: number, hidden?: string) {
  let defaultMessages: { [key: string]: string } = {
    "inc counter": "{sourcePlayer} increments {name} counter of {color} by {value}",
    "component move": "{sourcePlayer} moves {componentId} to {destinationId}",

    "component reveal": "{sourcePlayer} reveals {revealedComponent.id} from {containerId} to {destinationId}",
    "move to bank": "{sourcePlayer} moves {componentData.id} to {destination}",
    discard: "{sourcePlayer} discards {componentData.id}",
    "set component option": "{sourcePlayer} sets option {optionName} of {componentId} to {newValue}",
    "set variable": "{sourcePlayer} sets option {optionName} of {componentId} to {newValue}",
    "set gamestate": "Gamestate switched to {newState.name} ({newState.activePlayer} is now active)",
    "set global": "Global variable {variableName} set to {newValue}",
    "set player attr": "{color} attribute {attrName} is set to {newValue}",
    "press button": "{sourcePlayer} presses button {buttonName}",
    custom: "{sourcePlayer} makes custom move {custom name}",

    "inc stat": "",
    "push stat": " ",
  };
  let verbose = false;
  /*if (localStorage.getItem("server-env") === "dev") {
    verbose = true;
  }*/
  let eventCopy = Object.assign({}, event) as GameMoveForEval;
  let message = event.logMessage;
  if (!message && (verbose || event.editMode)) {
    message = defaultMessages[event.type ?? ""];
  }
  if (hidden) {
    message += "(hidden)";
  }
  let logDisplayComponent: PossiblyUnknownGameComponent | undefined = eventCopy.logDisplayComponent;

  return { message, event: eventCopy, depth, logDisplayComponent, currTurn: event.currTurn } as LogMessageData;
}

export function adjustComponentPool(componentPool: ComponentPool, decks?: { [key: string]: DeckInfo }) {
  if (!componentPool["player-area"]) {
    componentPool["player-area"] = {};
  }
  if (componentPool["__playerArea"]?.width) {
    componentPool["player-area"].width = componentPool["__playerArea"].width;
  }
  if (componentPool["__playerArea"]?.height) {
    componentPool["player-area"].height = componentPool["__playerArea"].height;
  }
  if (componentPool["__playerArea"]?.x) {
    componentPool["player-area"].x = componentPool["__playerArea"].x;
  }
  if (componentPool["__playerArea"]?.y) {
    componentPool["player-area"].y = componentPool["__playerArea"].y;
  }
  if (decks) {
    for (let deckName in decks) {
      if (!decks[deckName]) {
        continue;
      }
      let componentName = "__card-" + deckName;
      if (!componentPool[componentName]) {
        componentPool[componentName] = {};
      }
      componentPool[componentName].height = decks[deckName].cardInfo?.h;
      componentPool[componentName].width = decks[deckName].cardInfo?.w;
    }
  }

  componentPool["root"].selectable = false;
}

export function addChildData(gameSituation: GameSituationBackend) {
  addChildDataRec(gameSituation.root);
  if (gameSituation.players) {
    for (let p of gameSituation.players) {
      addChildDataRec(p.area);
    }
  }
}

function addChildDataRec(comp: GameComponent) {
  for (let positionName in comp.children) {
    for (let childPos in comp.children[positionName]) {
      let c = comp.children[positionName][childPos];
      if (Array.isArray(c)) {
        for (let i in c) {
          let component = c[i];
          let newData = Object.assign({}, component, { childData: { positionName, childPos, i } });
          c[i] = newData;
          if ((component as GameComponentForGame).children) {
            addChildDataRec(component as GameComponentForGame);
          }
        }
      } else {
        let newData = Object.assign({}, c, { childData: { positionName, childPos } });
        comp.children[positionName][childPos] = newData;
        addChildDataRec(comp.children[positionName][childPos] as GameComponent);
      }
    }
  }
}

export const audio: { [key: string]: HTMLAudioElement } = {
  ding: new Audio("/assets/sounds/ding.wav"),
  card: new Audio("/assets/sounds/cardPlay.wav"),
  wood: new Audio("/assets/sounds/woodCartonTap.wav"),
  gain: new Audio("/assets/sounds/plasticGain.wav"),
  chatPop: new Audio("/assets/sounds/chatPop.wav"),
};

audio.ding.volume = 0.5;

export function processComponentInfo(
  componentData: GeneralComponentForGame,
  overdrive?: ComponentDataSource,
  additionalOptions?: { [key: string]: any },
) {
  let componentOptions = componentData.options ?? {};
  if (additionalOptions) {
    mergeDeep(componentOptions, additionalOptions);
  }
  function conditionMet(conditions: { [key: string]: string }) {
    if (!conditions) {
      return;
    }
    let passed = true;
    for (let conditionName of Object.keys(conditions)) {
      if (Array.isArray(conditions[conditionName])) {
        if (!conditions[conditionName].includes(componentOptions[conditionName])) {
          passed = false;
        }
      } else if (componentOptions[conditionName] !== conditions[conditionName]) {
        passed = false;
      }
    }
    return passed;
  }
  let componentInfo = undefined;
  if (window.componentPool[componentData.name]) {
    componentInfo = JSON.parse(JSON.stringify(window.componentPool[componentData.name]));
  }
  if (!componentInfo && componentData.name.startsWith("card-")) {
    // This is just for backwards compatibility before __card
    componentInfo = JSON.parse(JSON.stringify(window.componentPool["__" + componentData.name]));
  }
  if (!componentInfo) {
    return {};
  }
  let resultComponentInfo = {};

  function reduceConditions(componentInfo: ComponentData) {
    if (typeof componentInfo !== "object") {
      return;
    }
    let keys = Object.keys(componentInfo ?? {});
    keys.sort();
    keys.reverse();
    for (let name of keys) {
      let resultValue = componentInfo[name];
      let finalName = name;

      let isCond =
        (Array.isArray(componentInfo[name]) && componentInfo[name][0]?.condition) || name.startsWith("__cond_");
      if (isCond) {
        finalName = name.slice(name.startsWith("__cond_") ? 7 : 0);
        resultValue = undefined;
        for (let option of Object.values(componentInfo[name]) as ComponentData[]) {
          if (conditionMet(option.condition)) {
            resultValue = option.result;
          }
        }
      }
      reduceConditions(resultValue);
      if (isCond) {
        delete componentInfo[name];
      }
      if (overdrive && overdrive[componentData.name] && overdrive[componentData.name][finalName]) {
        continue;
      }
      if (resultValue !== undefined) {
        componentInfo[finalName] = resultValue;
      }
    }
  }
  reduceConditions(componentInfo);
  if (overdrive) {
    reduceConditions(overdrive);
    mergeDeep(componentInfo, overdrive);
  }
  reduceConditions(componentInfo);
  return componentInfo;
}
